import Cookies from 'js-cookie';
import appSettings from '../config/settings';
import { breadcrumb, error as logError } from './log';

/**
 * Story 4.5.2 — tph-api handshake utility.
 *
 * Reads the Sharetribe access token from the JS-readable browser cookie
 * (`st-<clientId>-token`, populated by the SDK's default browserCookieStore
 * — verified at node_modules/sharetribe-flex-sdk/src/browser_cookie_store.js),
 * then performs a direct cross-origin GET to tph-api with
 * `Authorization: Bearer <access_token>`. tph-api's route-scoped CORS
 * middleware (Story 4.5.2 AC1) gates which marketplace origins may consume
 * `/api/v1/seller/*`.
 *
 * Error envelopes mapped per AC2:
 *   - missing/unparseable cookie  → throw new Error('NOT_AUTHENTICATED')
 *   - HTTP 401                    → throw new Error('TOKEN_REJECTED')
 *   - HTTP 5xx / network error    → throw new Error('TPH_API_UNAVAILABLE')
 */

const cookieKey = () => {
  const clientId = appSettings.sdk.clientId;
  // Sharetribe SDK default namespace is `st`; key format is `st-<clientId>-token`.
  return `st-${clientId}-token`;
};

const readAccessToken = () => {
  const raw = Cookies.get(cookieKey());
  if (!raw) {
    throw new Error('NOT_AUTHENTICATED');
  }
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    throw new Error('NOT_AUTHENTICATED');
  }
  const accessToken = parsed && parsed.access_token;
  if (!accessToken) {
    throw new Error('NOT_AUTHENTICATED');
  }
  return accessToken;
};

const baseUrl = () => {
  const v = process.env.REACT_APP_TPH_API_BASE_URL;
  return (v || '').replace(/\/$/, '');
};

/**
 * Story 10.19 (D2) — every `TPH_API_UNAVAILABLE` throw says WHY, without
 * changing what it is. The message is unchanged, so every caller that branches
 * on it behaves exactly as before. Two properties are added:
 *   - `failure`: 'network' (window.fetch rejected: CORS block, DNS, offline,
 *     TLS — tph-api never saw the request, the #497 shape), 'http' (a non-ok
 *     status), or 'body' (a 2xx whose body could not be read);
 *   - `httpStatus`, for 'http' only.
 * Ticket 35 adds 'timeout' (a status change that got no answer within
 * STATUS_CAPTURE_TIMEOUT_MS); clientFailureReport classifies it as the
 * existing `tph_api_unavailable`.
 *
 * 🔴 NOT `status`. `toTierUpgradeRejection` (EditListingPage.duck.js) copies
 * `e.status` into the tier modal's payload, so a `status` here would change
 * what the modal branches on.
 */
const unavailable = (failure, httpStatus) => {
  const err = new Error('TPH_API_UNAVAILABLE');
  err.failure = failure;
  if (failure === 'http') {
    err.httpStatus = httpStatus;
  }
  return err;
};

/**
 * GET /api/v1/seller/available-tiers — returns the parsed body
 * `{ availableTiers: [{tier, slots}], request_id }` per Story 4.5.1's
 * contract.
 */
export const fetchAvailableTiers = async () => {
  const accessToken = readAccessToken();
  let response;
  try {
    response = await window.fetch(`${baseUrl()}/api/v1/seller/available-tiers`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  } catch (e) {
    throw unavailable('network');
  }
  if (response.status === 401) {
    throw new Error('TOKEN_REJECTED');
  }
  if (response.status >= 500 || !response.ok) {
    throw unavailable('http', response.status);
  }
  try {
    return await response.json();
  } catch (e) {
    throw unavailable('body');
  }
};

/**
 * Ticket 35 send-back (Ryan's staging test, 2026-10-30) — the two laptop
 * failures, both in the status-change call below.
 *
 * 1. "Please sign in again" while still signed in. The access token is read
 *    straight from the Sharetribe cookie, and only the SDK's own calls refresh
 *    it, so a My Listings tab left open past the token's lifetime sent an
 *    expired one and tph-api answered 401 (Sentry, 2026-10-30 14:27 UTC,
 *    listing_cancel / token_rejected). The browser entry (src/index.js)
 *    registers a refresher — `sdk.currentUser.show()`, whose 401 makes the SDK
 *    trade the refresh token for a new access token and write it to the same
 *    cookie. On a 401 the call runs it once, re-reads the cookie and retries
 *    once. A refused refresh (the seller really is signed out) or a second 401
 *    is TOKEN_REJECTED, exactly as before.
 * 2. A hold that sat stuck and saved nothing, with no failure report: the
 *    request never settled, and window.fetch has no time limit. Each step now
 *    gives up after STATUS_CAPTURE_TIMEOUT_MS: the request (and reading its
 *    body), the refresh, and the retry (and reading its body). So the worst
 *    case is first request + refresh + retry, each bounded at 45 s — not a
 *    single 45 s. 45 s covers tph-api's own upstream timeouts plus its Stripe
 *    work; a hold that really lands after it shows on the next refetch (the
 *    card refetches after a timeout).
 *
 * Two status calls that get a 401 at the same time share ONE refresh: the
 * SDK may rotate the refresh token, so a second, parallel refresh could be
 * refused and show "Please sign in again" to a seller who is signed in.
 */
export const STATUS_CAPTURE_TIMEOUT_MS = 45 * 1000;

let accessTokenRefresher = null;

/**
 * Register (or, with null, clear) the function that refreshes the Sharetribe
 * access token in the cookie. Returns a promise; rejects when the refresh
 * failed.
 *
 * @param {function|null} fn
 */
export const setAccessTokenRefresher = fn => {
  accessTokenRefresher = typeof fn === 'function' ? fn : null;
  refreshInFlight = null;
};

// The one refresh in flight, shared by every call that got a 401 meanwhile;
// cleared when it settles.
let refreshInFlight = null;

// Reads a response body within the same limit. A body that stalls after the
// headers is TPH_API_UNAVAILABLE, failure 'timeout'.
const readBodyWithinLimit = response =>
  withinTimeLimit(new Promise(resolve => resolve(response.json())), () => unavailable('timeout'));

const isTimeout = e => !!e && e.message === 'TPH_API_UNAVAILABLE' && e.failure === 'timeout';

// Settles as `promise` does, or rejects with `onTimeout()` after the limit.
// The timer is cleared whichever way it settles.
const withinTimeLimit = (promise, onTimeout) => {
  let timer;
  const timeout = new Promise((resolve, reject) => {
    timer = setTimeout(() => reject(onTimeout()), STATUS_CAPTURE_TIMEOUT_MS);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

// One POST to status-capture, bounded. Resolves with the response; rejects
// with TPH_API_UNAVAILABLE ('network' or 'timeout').
const postStatusCapture = (listingUuid, body, accessToken) => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const request = new Promise(resolve =>
    resolve(
      window.fetch(`${baseUrl()}/api/v1/listings/${listingUuid}/status-capture`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        signal: controller ? controller.signal : undefined,
      })
    )
  ).catch(() => {
    throw unavailable('network');
  });
  return withinTimeLimit(request, () => {
    // Drop the connection too, where the browser supports it.
    if (controller) controller.abort();
    return unavailable('timeout');
  });
};

// The Sharetribe API refuses a refresh with 401 (bad or revoked refresh
// token) or 403 (no signed-in user); 400 is an invalid grant.
const isRefusedRefresh = e => !!e && (e.status === 400 || e.status === 401 || e.status === 403);

// Low-cardinality Sentry tags (log.js `error`, 4th argument; TPH_Pipeline §9b).
export const TOKEN_REFRESH_FAILURE_TAGS = {
  feature: 'listing_status_change',
  ticket: '35',
  provider: 'sharetribe',
  operation: 'token_refresh',
};

const REFRESH_TIMEOUT = 'TOKEN_REFRESH_TIMEOUT';

// Runs the registered refresher once. Resolves when it worked; otherwise
// throws TOKEN_REJECTED. A refusal is the signed-out case and is not an
// error; any other failure (network, 5xx, timeout) is reported to Sentry —
// with a fresh Error, never the SDK's, so no token or request can ride along.
const runRefresh = async () => {
  try {
    await withinTimeLimit(new Promise(resolve => resolve(accessTokenRefresher())), () =>
      Object.assign(new Error(REFRESH_TIMEOUT), { name: 'TimeoutError' })
    );
  } catch (e) {
    if (!isRefusedRefresh(e)) {
      const status = e && typeof e.status === 'number' ? e.status : null;
      const reason = e && e.message === REFRESH_TIMEOUT ? 'timeout' : 'failed';
      logError(
        new Error('Sharetribe access token refresh failed'),
        'sharetribe_token_refresh_failed',
        { refreshStatus: status, reason },
        TOKEN_REFRESH_FAILURE_TAGS
      );
    }
    throw new Error('TOKEN_REJECTED');
  }
};

// Every 401 that arrives while a refresh is running waits for that refresh.
const refreshAccessToken = () => {
  if (!refreshInFlight) {
    refreshInFlight = runRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

/**
 * Story 7.10.1 — seller-initiated listing status change.
 *
 * POSTs to the (DONE) tph-api Story 7.10 route
 * `POST /api/v1/listings/:listing_uuid/status-capture`, which dispatches on
 * the `target_status` discriminator (`on_hold | active | sold | cancelled`)
 * and owns ALL billing/Stripe/cascade/idempotency. This client only relays.
 *
 * Reuses the same Bearer-from-cookie auth + error envelope as
 * `fetchAvailableTiers`, adding `Content-Type: application/json` for the body.
 *
 * Error contract (AC6):
 *   - missing/unparseable cookie → throw Error('NOT_AUTHENTICATED')
 *   - HTTP 401                   → throw Error('TOKEN_REJECTED') (ticket 35:
 *                                  after one token refresh and one retry, when
 *                                  a refresher is registered)
 *   - HTTP 403/409/422           → throw a TYPED error: `err.status` + `err.code`
 *                                  (parsed from `{ error: { code } }`) so the
 *                                  caller can show the AC6 copy and treat a
 *                                  `409 already_sold` as success-by-refetch.
 *   - any other non-2xx / 5xx / network → throw Error('TPH_API_UNAVAILABLE')
 *   - no answer (or no body) within STATUS_CAPTURE_TIMEOUT_MS →
 *                                  TPH_API_UNAVAILABLE, `failure: 'timeout'`
 *                                  (ticket 35)
 *
 * @param {string} listingUuid - The Sharetribe listing UUID.
 * @param {Object} body - The status-capture body (see helpers below).
 * @returns {Promise<Object>} parsed success body (200), or `{}` if no JSON.
 */
export const captureListingStatus = async (listingUuid, body) => {
  let response = await postStatusCapture(listingUuid, body, readAccessToken());

  // Ticket 35 — an expired access token. One refresh, one retry: a 401 means
  // tph-api did nothing, so the retry cannot double a hold or a cancel.
  if (response.status === 401 && accessTokenRefresher) {
    await refreshAccessToken();
    response = await postStatusCapture(listingUuid, body, readAccessToken());
  }

  if (response.status === 401) {
    throw new Error('TOKEN_REJECTED');
  }

  if (response.ok) {
    try {
      return await readBodyWithinLimit(response);
    } catch (e) {
      if (isTimeout(e)) throw e;
      // The server may return an empty body on success; that is still a success.
      return {};
    }
  }

  // Typed 4xx the page knows how to handle (owner / already-sold / bad transition).
  if (response.status === 403 || response.status === 409 || response.status === 422) {
    let code;
    try {
      const json = await readBodyWithinLimit(response);
      code = json && json.error && json.error.code;
    } catch (e) {
      if (isTimeout(e)) throw e;
      code = undefined;
    }
    const err = new Error(code || 'STATUS_CAPTURE_FAILED');
    err.status = response.status;
    err.code = code;
    throw err;
  }

  // 400 / 404 / 5xx / anything else → treat as unavailable.
  throw unavailable('http', response.status);
};

// ---- Thin helpers for the four target_status bodies (AC6) ---------------- //
// NOTE: the sold helper never sends `channel_of_sale` — the server derives
// it (and the legacy sold fields) from the answer.

/** Put a listing on hold with a public `hold_reason`. */
export const putListingOnHold = (listingUuid, holdReason) =>
  captureListingStatus(listingUuid, { target_status: 'on_hold', hold_reason: holdReason });

/** Reactivate an on-hold listing. */
export const reactivateListing = listingUuid =>
  captureListingStatus(listingUuid, { target_status: 'active' });

/**
 * Ticket 36 (D9) — mark a listing sold with the sold screen's answer:
 * `{ q1_answer, buyer_ref?, q2_answer?, q2_sub_choice? }`. Only those four
 * keys are sent (never channel_of_sale — the server derives the legacy
 * fields); an absent optional key is omitted, not sent as null.
 */
export const markListingSold = (listingUuid, answer) => {
  const a = answer || {};
  const body = { target_status: 'sold', q1_answer: a.q1_answer };
  ['buyer_ref', 'q2_answer', 'q2_sub_choice'].forEach(k => {
    if (a[k]) {
      body[k] = a[k];
    }
  });
  return captureListingStatus(listingUuid, body);
};

/**
 * Ticket 36 (D8) — the sold screen's buyer list:
 * GET /api/v1/listings/:listing_uuid/sold-buyers?offset&limit →
 * `{ request_id, total, view_count, buyers: [{ ref, display_name, channel,
 * line_last4, last_contact_at }] }`. Opening the screen asks for offset 0,
 * limit 3; "Show everyone who reached out" asks for offset 3, limit 100.
 *
 * Same Bearer-from-cookie auth and error envelope as `fetchAvailableTiers`.
 */
export const fetchSoldBuyers = async (listingUuid, { offset = 0, limit = 3 } = {}) => {
  const accessToken = readAccessToken();
  const qs = `offset=${encodeURIComponent(offset)}&limit=${encodeURIComponent(limit)}`;
  let response;
  try {
    response = await window.fetch(`${baseUrl()}/api/v1/listings/${listingUuid}/sold-buyers?${qs}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  } catch (e) {
    throw unavailable('network');
  }
  if (response.status === 401) {
    throw new Error('TOKEN_REJECTED');
  }
  if (!response.ok) {
    throw unavailable('http', response.status);
  }
  try {
    return await response.json();
  } catch (e) {
    throw unavailable('body');
  }
};

/**
 * Cancel a listing (server closes the Sharetribe listing + tears down billing).
 *
 * Ticket 35 (D5) — the seller's reason rides along: `cancel_reason` (one of
 * the five server keys), and each text only when it has something in it (an
 * empty box is omitted, not sent as null). `already_sold` never cancels (AC2:
 * it hands off to the sold screen), so it is refused here with no request
 * sent. Its code is NOT 'already_sold', which runStatusChange reads as success.
 */
export const cancelListing = (listingUuid, { cancelReason, otherText, feedbackText } = {}) => {
  if (cancelReason === 'already_sold') {
    const err = new Error('already_sold is not a cancel reason');
    err.code = 'invalid_request';
    return Promise.reject(err);
  }
  const body = { target_status: 'cancelled' };
  if (cancelReason) {
    body.cancel_reason = cancelReason;
  }
  if (typeof otherText === 'string' && otherText.trim() !== '') {
    body.other_text = otherText;
  }
  if (typeof feedbackText === 'string' && feedbackText.trim() !== '') {
    body.feedback_text = feedbackText;
  }
  return captureListingStatus(listingUuid, body);
};

/**
 * Epic 13 (Story 13.4) — tier-upgrade preview + execute relay.
 *
 * Two routes on tph-api own ALL billing logic; this client only relays:
 *   - POST /api/v1/listings/:listing_uuid/tier-upgrade/preview
 *       → returns a Stripe proration/invoice PREVIEW for THIS seller's
 *         subscription: `{ amount_due, target_monthly, currency, ... }`
 *         (amounts are minor units / Stripe shape — the modal formats them).
 *   - POST /api/v1/listings/:listing_uuid/tier-upgrade
 *       → executes the change (charge proration / free-switch / route-to-buy is
 *         the server's call) and returns the resulting tier.
 *
 * Both clone `captureListingStatus`'s Bearer-from-cookie auth + Content-Type +
 * typed-error envelope: 401 → TOKEN_REJECTED; ANY non-ok response carrying a
 * parsable `error.code` → a TYPED error carrying `err.status`, `err.code` and
 * `err.reason` (from `{ error: { code, reason } }`) so the modal can branch on
 * payment_failed / already-owned / ineligible / config_fault, and can split
 * CG-8 from CG-10 on `reason`; a typed-4xx status (402/403/409/422) with NO
 * parsable code → TIER_UPGRADE_FAILED carrying its status; everything else,
 * including every untyped 5xx and any network failure → TPH_API_UNAVAILABLE.
 *
 * NOTE 13.4-1 (cold review 2026-09-08) — this block previously read "everything
 * else (400/404/untyped-5xx/network) → TPH_API_UNAVAILABLE", which Story 13.4 T1
 * made FALSE: 400 `invalid_request` and 404 `listing_not_found` carry a real
 * error.code and are now typed, which is the whole point of T1. It also claimed
 * these endpoints were HELD LOCAL / not yet on staging; they have been live in
 * production since 2026-07-30. Both sentences are corrected here rather than
 * left to mislead the next reader — the same reason T2 required the duck's
 * comment be rewritten.
 *
 * @param {string} listingUuid - The Sharetribe listing UUID.
 * @param {Object} body - `{ target_tier, current_tier }`.
 * @returns {Promise<Object>} parsed success body (200), or `{}` if no JSON.
 */
const postTierUpgrade = async (path, listingUuid, body) => {
  const accessToken = readAccessToken();
  let response;
  try {
    response = await window.fetch(`${baseUrl()}/api/v1/listings/${listingUuid}/${path}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    throw unavailable('network');
  }

  if (response.status === 401) {
    throw new Error('TOKEN_REJECTED');
  }

  if (response.ok) {
    try {
      return await response.json();
    } catch (e) {
      return {};
    }
  }

  // Typed errors the modal maps to its own inline copy:
  //   402 payment_failed   — card declined (money-path code; must NOT collapse
  //                          into the generic "unavailable" state). Carries
  //                          `reason`, which distinguishes a plain decline from
  //                          the swapped-but-unpaid `revert_failed` path.
  //   403 / 409 / 422      — already-owned / ineligible / no-eligible-slot etc.
  //   400 / 404            — invalid_request / listing_not_found. Story 13.4 T1:
  //                          these carry a real `error.code` and used to be
  //                          discarded by the blanket fallthrough at the bottom
  //                          of this function, so the modal could not tell them
  //                          apart from a network drop.
  //   500 config_fault     — surfaced ONLY when the server tags `error.code`;
  //                          an untyped 5xx still degrades to TPH_API_UNAVAILABLE
  //                          (the graceful state).
  //
  // T1 — parse the typed envelope for ANY non-ok response (401 already returned
  // above). `reason` rides along wherever the server sends it: the 402
  // payment_failed and 502 collect_failed envelopes carry it, and the
  // swapped-but-unpaid revert path sends reason:'revert_failed'.
  let code;
  let reason;
  try {
    const json = await response.json();
    const error = json && json.error;
    code = error && error.code;
    reason = error && error.reason;
  } catch (e) {
    code = undefined;
    reason = undefined;
  }

  if (code) {
    const err = new Error(code);
    err.status = response.status;
    err.code = code;
    err.reason = reason != null ? reason : null;
    throw err;
  }

  // No parsable code. A typed-4xx status still means something the modal maps,
  // so it keeps its TIER_UPGRADE_FAILED vocabulary; every other status —
  // including every untyped 5xx — degrades to the graceful "unavailable" state.
  const isTyped4xx =
    response.status === 402 ||
    response.status === 403 ||
    response.status === 409 ||
    response.status === 422;
  if (isTyped4xx) {
    const err = new Error('TIER_UPGRADE_FAILED');
    err.status = response.status;
    err.code = undefined;
    err.reason = null;
    throw err;
  }

  throw unavailable('http', response.status);
};

/** Preview the proration/invoice for moving listingUuid from currentTier → targetTier. */
export const previewTierUpgrade = ({ listing_uuid, targetTier, currentTier }) =>
  postTierUpgrade('tier-upgrade/preview', listing_uuid, {
    target_tier: targetTier,
    current_tier: currentTier,
  });

/**
 * Execute the tier change for listingUuid (server owns charge vs free-switch vs buy).
 *
 * Story 13.7 (upgrade-origin addition) — `origin` is the modal's `feature`
 * prop: WHICH locked surface sent the seller here. It rides through to the
 * internal upgrade alert only; it changes no pricing, no eligibility and no
 * response. Omitted when absent — `JSON.stringify` drops `undefined` keys, so
 * an old cached bundle simply posts the two fields it always posted and the
 * server renders the origin as `unknown` rather than guessing.
 */
export const executeTierUpgrade = ({ listing_uuid, targetTier, currentTier, origin }) =>
  postTierUpgrade('tier-upgrade', listing_uuid, {
    target_tier: targetTier,
    current_tier: currentTier,
    upgrade_origin: origin,
  });

// ─── Story 9.17 — the public email-status lookup ─────────────────────────────

// D2 — the three enum values are the ENTIRE vocabulary. There is no fourth
// value: a lookup failure arrives as an HTTP status and degrades to null here.
const EMAIL_STATUS_ENUM = ['migrated_passwordless', 'account_exists', 'none'];

// The client-side abort. Matches the server's own budget so a browser that has
// already given up is not still holding a connection open for one that has not.
const EMAIL_STATUS_TIMEOUT_MS = 2500;

/**
 * OQ4 — ONE greppable event name shared with tph-api
 * (`email_status_lookup_degraded`), plus a Sentry breadcrumb. Reason enum:
 * timeout | overflow | cors | 4xx | 5xx.
 *
 * `cors` can ONLY be observed here. A CORS block produces no log line, no
 * alert and no error row server-side (deferred-work #497), and this story's
 * fallback is deliberately silent (AC5.3), so no user reports it either. This
 * breadcrumb is the whole of the detection surface for that failure mode.
 *
 * Never carries the email address (D2).
 */
const logEmailStatusDegraded = reason => {
  breadcrumb('tphApi', 'email_status_lookup_degraded', { reason });
};

/**
 * Story 9.17 — POST /api/v1/public/auth/email-status.
 *
 * 🔴 THIS ROUTE IS PUBLIC AND THE CALL IS DELIBERATELY DIRECT. It does NOT go
 * through a marketplace same-origin proxy, and that is load-bearing rather than
 * stylistic. `server/api/contact.js`'s own module header records the 2026-07-23
 * incident: setting a visitor-IP header on an outbound request from this server
 * tripped Cloudflare-in-front-of-Render's loop/spoof detection (403, error 1000)
 * before the request reached tph-api, and Cloudflare SETS its own value — which
 * is this server's egress IP. Behind a proxy, tph-api's per-IP rate limit
 * therefore keys every visitor onto ONE SHARED BUCKET. That limit is one of the
 * two mitigations the endpoint's security acceptance rests on (and the only one
 * covering the legacy-cohort bit), so proxying this call would silently void it
 * while every unit test stayed green.
 *
 * There is a test in this repo that greps `server/` for `email-status` and
 * reddens if a proxy ever appears. If you are here because that test failed,
 * read Story 9.17 GT4 before "fixing" it.
 *
 * ⚠️ NO `Authorization` HEADER. The route is public and the seller token must
 * never be attached to it (Fence 5). `readAccessToken` is not called here.
 *
 * 🔴 NO RETRY, NO BACKOFF, EVER (AC5.4 / N4). One login or signup failure → AT
 * MOST ONE window.fetch. Two reasons, and the second is why this is a hard rule
 * and not a preference:
 *   (a) retrying an endpoint that is degraded BECAUSE IT IS LOADED multiplies
 *       exactly the load that degraded it;
 *   (b) server-side, every abandoned attempt leaves a LIVE upstream request
 *       behind — the Integration SDK takes no timeout option (deferred-work
 *       #127), so tph-api can only stop waiting, not cancel. A retry therefore
 *       does not replace the first attempt, it ADDS to it, and a client-side
 *       retry loop becomes a client-side amplifier pointed at the Integration
 *       API. A 429 in particular is never retried; it is the system saying stop.
 *
 * NEVER THROWS. Any failure at all → `{ status: null }`, following the
 * `probeSlotClaim` discipline in src/util/api.js:228-242. The caller renders
 * exactly what it renders today and the user cannot tell a lookup was attempted.
 *
 * @param {string} email
 * @returns {Promise<{status: string|null}>} status ∈
 *   'migrated_passwordless' | 'account_exists' | 'none' | null
 */
export const fetchEmailStatus = async email => {
  const base = baseUrl();
  if (!base) {
    // No REACT_APP_TPH_API_BASE_URL — degrade silently, exactly as if the
    // lookup had failed. Do not construct a same-origin URL as a fallback:
    // that is the proxy transport, arrived at by accident.
    logEmailStatusDegraded('5xx');
    return { status: null };
  }

  // ⚠️ This one IS a REAL abort — window.fetch honours the signal and the
  // browser drops the connection. It is NOT the server's abandon (which frees
  // nothing, per #127). The client aborts; the server abandons. Do not conflate
  // them when reading the two timeouts side by side.
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), EMAIL_STATUS_TIMEOUT_MS) : null;

  let response;
  try {
    response = await window.fetch(`${base}/api/v1/public/auth/email-status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
      signal: controller ? controller.signal : undefined,
    });
  } catch (e) {
    // A CORS block and a network failure are indistinguishable here by design
    // (the browser refuses to tell page JS which it was). `cors` is the honest
    // label: deferred-work #497 is the outage where a CORS block was INVISIBLE
    // server-side, and this is the only place in either repo that can observe
    // one at all.
    logEmailStatusDegraded(e && e.name === 'AbortError' ? 'timeout' : 'cors');
    return { status: null };
  } finally {
    if (timer) clearTimeout(timer);
  }

  // `!response` is not paranoia: this helper's contract is that it NEVER
  // throws (AC5.3), and a thrown TypeError here would propagate into the
  // login-failure render path — a visible break on the exact surface the story
  // exists to make calmer. Any shape that is not a usable response is a degrade.
  if (!response || !response.ok) {
    const status = response ? response.status : 0;
    logEmailStatusDegraded(status >= 500 || status === 0 ? '5xx' : '4xx');
    return { status: null };
  }

  let body;
  try {
    body = await response.json();
  } catch (e) {
    logEmailStatusDegraded('5xx');
    return { status: null };
  }

  const status = body && typeof body.status === 'string' ? body.status : null;
  if (!EMAIL_STATUS_ENUM.includes(status)) {
    // An unrecognised value is a degrade, not a fourth enum. Never branch on it.
    logEmailStatusDegraded('5xx');
    return { status: null };
  }
  return { status };
};
